/**
 * roles.ts — fonte única de verdade para roles no frontend.
 *
 * REGRA: nunca compare role com string literal fora desse arquivo. Use
 * `ROLES.X` ou os predicados (hasRole, hasAnyRole, isAdmin).
 *
 * Renomear uma role exige:
 *   1. mudar o valor da constante aqui
 *   2. mudar o valor correspondente em api/.../middleware/roles.ts (backend)
 *   3. nova migration SQL para `is_admin()` em 003_rls_policies.sql
 *   4. UPDATE em app_metadata.roles dos users existentes via service_role
 */

export const ROLES = {
  USER: 'user',
  ADMIN: 'admin',
  AVALIADOR: 'avaliador',
  SUPERADMIN: 'superadmin',
  ADMIN_BC: 'admin-background-check',
  AVALIADOR_BC: 'avaliador-background-check',
  ADMIN_FINANCEIRO: 'admin-financeiro',
} as const

export type Role = (typeof ROLES)[keyof typeof ROLES]

export const ROLE_LABELS: Record<Role, string> = {
  user: 'Usuário',
  admin: 'Administrador',
  avaliador: 'Avaliador',
  superadmin: 'Super Admin',
  'admin-background-check': 'Admin Background Check',
  'avaliador-background-check': 'Avaliador Background Check',
  'admin-financeiro': 'Admin Financeiro',
}

export function hasRole(roles: readonly unknown[] | undefined, role: Role): boolean {
  if (!Array.isArray(roles)) return false
  return roles.includes(role)
}

function hasAnyRole(
  roles: readonly unknown[] | undefined,
  ...wanted: Role[]
): boolean {
  if (!Array.isArray(roles)) return false
  return wanted.some((r) => roles.includes(r))
}

export const isAdmin = (roles: readonly unknown[] | undefined) => hasRole(roles, ROLES.ADMIN)
export const isAvaliador = (roles: readonly unknown[] | undefined) => hasRole(roles, ROLES.AVALIADOR)
export const isAdminOrAvaliador = (roles: readonly unknown[] | undefined) =>
  hasAnyRole(roles, ROLES.ADMIN, ROLES.AVALIADOR)
export const isAdminBc = (roles: readonly unknown[] | undefined) => hasRole(roles, ROLES.ADMIN_BC)
export const isAvaliadorBc = (roles: readonly unknown[] | undefined) => hasRole(roles, ROLES.AVALIADOR_BC)
export const isAdminFinanceiro = (roles: readonly unknown[] | undefined) => hasRole(roles, ROLES.ADMIN_FINANCEIRO)
